/**
 * @file NarrativeWorkbench.js
 * @description Panelized workbench for narrative elements.
 */

import React, { useMemo, useRef, useState, useEffect } from 'react';
import { renderScreenplayForTile } from '../utilities/utility';
import ScreenplayTile from './ScreenplayTile';
import CharacterSlideshow from './CharacterSlideshow';
import SceneTile from './SceneTile';
import VideoTile from './VideoTile';
import MergedVideoTile from './MergedVideoTile';
import './NarrativeWorkbench.css';



// Custom Mini Player for transparent background support
const MiniPlayer = ({ src, disabled }) => {
  const audioRef = React.useRef(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      const val = (audio.currentTime / audio.duration) * 100;
      setProgress(isNaN(val) ? 0 : val);
    };

    const onEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const togglePlay = () => {
    if (disabled || !src) return;
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="mini-player" style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '4px 0px', // Reduced side padding since no pill shape
      borderRadius: '20px',
      background: 'transparent',
      border: 'none',
      width: 'fit-content',
      opacity: disabled ? 0.5 : 1,
      pointerEvents: disabled ? 'none' : 'auto',
    }}>
      <audio ref={audioRef} src={src} />
      <button
        onClick={(e) => {
          e.stopPropagation();
          togglePlay();
        }}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#4caf50', // Green accent
          padding: 0,
        }}
      >
        {isPlaying ? (
          // Pause Icon
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" rx="1" />
            <rect x="14" y="4" width="4" height="16" rx="1" />
          </svg>
        ) : (
          // Play Icon
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>

      {/* Simple Progress Bar */}
      <div style={{
        width: '100px',
        height: '4px',
        background: 'rgba(255,255,255,0.2)',
        borderRadius: '2px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: `${progress}%`,
          background: '#4caf50',
          transition: 'width 0.1s linear'
        }} />
      </div>
    </div>
  );
};

const NarrativeWorkbench = ({
  // ── Screenplay props
  detailedScreenplay,
  isScreenplayLoading,
  screenplayOutline,
  isDetailsGenerating,
  onGenerateScenes,
  isScenesGenerating,
  isEditing,
  onSaveEditedScreenplay,
  maximizeScreenPlay,
  toggleMaximizeScreenPlay,
  onCancelEdit,
  onValidateContent,

  // Screenplay header tool actions
  onRegenerateScreenplay,
  onEditScreenplay,
  onDownloadScreenplay,
  onEnhanceScreenplay,

  // ── Characters props
  characterBible,
  isCharacterBibleLoading = false,
  isCharacterEnriching,
  setIsOverlayVisible,
  onSaveCharacter,
  showCharactersPanel = false,
  onRegenerateCharacterFull, // NEW
  characterIsRegenerating,   // NEW

  // ── Scenes props
  scenes = [],
  showScenes = false,
  onToggleShowScenes,                // () => void
  isScenesGeneratingPanel = false,   // defaults to isScenesGenerating if undefined/null
  videoIsLoading = [],
  videoDots = [],
  regenerateImage,
  regenerateImagePostEdit,
  imageIsRegenerating = {},
  onSelectSceneImage, // NEW prop
  generateVideo,
  regenerateVideo,
  regenerateVideoPostEdit,
  onRegenerateShot, // NEW
  onSmoothShot, // NEW
  onSelectShotVideo, // NEW
  isMerging = {}, // NEW
  showScenesPanel = false,

  // ── Final Movie props
  mergedVideoUrl,
  modelOptions, // NEW prop
  showFinalMoviePanel = false,
  finalMovieTitle = '',
  onMergeScenes, // NEW
  isGlobalMerging = false, // NEW

  // Optional loading asset
  loadingGifUrl,
  hydratedScenesByNumber,
  projectId
}) => {
  /* ─── State for Context Menu (Lifted) ─── */
  const [contextMenu, setContextMenu] = useState(null);

  const charactersForView = useMemo(() => {
    if (!characterBible || typeof characterBible.size !== 'number') return [];
    const loader = loadingGifUrl || '/images/loading.gif'; // fallback if not provided
    return Array.from(characterBible.entries()).map(([name, details]) => {
      // Do NOT mutate the original; clone for view-only substitution
      const showImage = details?.image || (isCharacterEnriching ? loader : null);
      return {
        name,
        details: {
          ...details,
          // If enrichment is ongoing and we don't yet have an image, show the spinner GIF
          image: showImage
        }
      };
    });
  }, [characterBible, isCharacterEnriching, loadingGifUrl]);

  const _displayText = useMemo(() => {
    if (!detailedScreenplay) return '';
    return (typeof detailedScreenplay === 'object')
      ? renderScreenplayForTile(detailedScreenplay)
      : String(detailedScreenplay);
  }, [detailedScreenplay]);

  const lines = _displayText.split('\n');

  const _isScenesGenerating =
    (typeof isScenesGeneratingPanel === 'boolean' ? isScenesGeneratingPanel : isScenesGenerating);

  // === Screenplay "Show More" control in PANEL FOOTER (keeps ScreenplayTile logic)
  const screenplayTileHostRef = useRef(null);
  const [screenplayExpanded, setScreenplayExpanded] = useState(false);
  const screenplayHasMore = (_displayText?.split('\n')?.length || 0) > 10 || ((screenplayOutline?.scenes?.length || 0) > 5);

  const toggleScreenplayExpand = () => {
    try {
      const root = screenplayTileHostRef.current;
      const btn = root?.querySelector('.show-more-button');
      if (btn) btn.click();
    } catch (_) { }
    setScreenplayExpanded(prev => !prev);
  };

  const handleScenesPlayToggle = () => {
    const willShow = !showScenes;
    setScenesCollapsed(!willShow);
    onToggleShowScenes?.();
  };

  // === Per-panel collapse state ===
  const [isScreenplayCollapsed, setScreenplayCollapsed] = useState(false);
  const [isCharactersCollapsed, setCharactersCollapsed] = useState(false);
  const [isScenesCollapsed, setScenesCollapsed] = useState(false);
  const [isFinalCollapsed, setFinalCollapsed] = useState(false);

  // === Dampen Zoom Aggression ===
  // Counteract browser zoom so 200% zoom feels like 150%
  React.useLayoutEffect(() => {
    const updateScale = () => {
      const dpr = window.devicePixelRatio || 1;
      // Formula: Scale = 0.75 * (1 + 0.5 * (dpr - 1)) / dpr
      const desiredVisualMultiplier = 1 + 0.5 * (dpr - 1); // 1.0 -> 1x, 2.0 -> 1.5x
      const newScale = (0.75 * desiredVisualMultiplier) / dpr;

      const newWidth = 100 / newScale;

      const workbench = document.querySelector('.narrative-workbench');
      if (workbench) {
        workbench.style.setProperty('--nw-scale', newScale);
        workbench.style.setProperty('--nw-width', `${newWidth}%`);
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  // === NEW: Lifted UI State for Scenes (Mode, History, Slideshow) ===
  const sceneUIStateRef = useRef({}); // { [index]: { mode, showHistory, showSlideshow } }
  const [, _forceUpdate] = useState();
  const forceUpdate = () => _forceUpdate({});

  const hasScenes = Array.isArray(scenes) && scenes.some(s => s?.image || s?.video);
  const shouldRenderScenesPanel = showScenesPanel && (hasScenes || _isScenesGenerating);

  // === NEW: Auto-switch to Video Mode when generation completes ===
  const prevVideoLoadingRef = useRef(videoIsLoading);
  React.useEffect(() => {
    const prevLoading = prevVideoLoadingRef.current || [];
    const currLoading = videoIsLoading || [];

    // Check for any index that went from loading (true) -> not loading (false)
    scenes.forEach((scene, index) => {
      const wasLoading = !!prevLoading[index];
      const isLoading = !!currLoading[index];

      if (wasLoading && !isLoading) {
        // Generation finished. Check if we actually have a video now.
        if (scene.video) {
          // Force switch to video mode
          const prevState = sceneUIStateRef.current[index] || {};
          if (prevState.mode !== 'video') {
            sceneUIStateRef.current[index] = { ...prevState, mode: 'video' };
            forceUpdate();
          }
        }
      }
    });

    prevVideoLoadingRef.current = currLoading;
  }, [videoIsLoading, scenes]);

  // Unified Shot Matrix Header
  const ShotMatrixHeader = () => (
    <thead>
      <tr>
        <th style={{ width: '40px' }}>Beat</th>
        <th style={{ width: '50px' }}>ID</th>
        <th style={{ width: '50px' }}>Dur</th>
        <th style={{ width: '120px' }}>Shot Type</th>
        <th style={{ width: '120px' }}>Camera</th>
        <th style={{ width: '120px' }}>Lighting</th>
        <th style={{ width: '120px' }}>Movement</th>
        <th>Visual</th>
        <th style={{ width: '150px' }}>Dialogue</th>
        <th>Audio</th>
      </tr>
    </thead>
  );

  // Unified details strip under the media pair
  const SceneDetailsStrip = ({ sceneData, index }) => {
    const [isExpanded, setIsExpanded] = useState(false); // New state
    const detailsRef = useRef(null);

    // Auto-scroll when expanded
    useEffect(() => {
      if (isExpanded && detailsRef.current) {
        // Wait a tick for layout paint
        setTimeout(() => {
          detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      }
    }, [isExpanded]);

    const { duration } = sceneData; // Keep basic destructure
    const additionalInfo = sceneData.additionalInfo || {}; // Defensive default
    const sceneBible =
      sceneData.sceneBible ||
      additionalInfo.sceneBible ||
      additionalInfo.scene_bible ||
      null;
    const shotMatrix =
      sceneData.shotMatrix ||
      additionalInfo.shotMatrix ||
      additionalInfo.shot_matrix ||
      [];

    return (
      <div
        ref={detailsRef}
        className="nw-scene-details"
        onClick={() => {
          const selection = window.getSelection();
          if (selection && selection.toString().length > 0) return;
          setIsExpanded(!isExpanded);
        }}
        style={{ cursor: 'pointer' }}
        title={isExpanded ? "Click to collapse" : "Click to expand details"}
      >
        <div className="nw-scene-details-left">

          <div className="nw-info-grid-row">
            <div className="nw-info-block small">
              <div className="nw-label">Duration</div>
              <div className="nw-value">{duration}</div>
            </div>
            <div className="nw-info-block small">
              <div className="nw-label">Characters</div>
              <div className="nw-value">{(additionalInfo.characters || []).join(' · ')}</div>
            </div>
          </div>

          <div className="nw-info-block">
            <div className="nw-label">Scene Synopsis</div>
            <div className="nw-value">{additionalInfo.synopsis}</div>
          </div>

          {isExpanded && (
            <>
              <div className="nw-info-block">
                <div className="nw-label">Visual Prompt</div>
                <div className="nw-value">{additionalInfo.visual}</div>
              </div>

              <div className="nw-info-block">
                <div className="nw-label">Environment</div>
                <div className="nw-value">{additionalInfo.environment}</div>
              </div>

              {sceneBible && (
                <div className="nw-info-block">
                  <div className="nw-label">Scene Bible</div>
                  <ul className="nw-list">
                    {sceneBible.lens_mm ? <li><span className="nw-list-label">Lens:</span> {sceneBible.lens_mm}mm</li> : null}
                    {sceneBible.aspect ? <li><span className="nw-list-label">Aspect:</span> {sceneBible.aspect}</li> : null}
                    {sceneBible.time_of_day ? <li><span className="nw-list-label">Time:</span> {sceneBible.time_of_day}</li> : null}
                    {sceneBible.palette ? <li><span className="nw-list-label">Palette:</span> {sceneBible.palette}</li> : null}
                    {sceneBible.grade ? <li><span className="nw-list-label">Grade:</span> {sceneBible.grade}</li> : null}
                    {Array.isArray(sceneBible.continuity_notes) && sceneBible.continuity_notes.length ? (
                      <li><span className="nw-list-label">Continuity:</span> {sceneBible.continuity_notes.join('; ')}</li>
                    ) : null}
                  </ul>
                </div>
              )}

              {additionalInfo.coveragePlan && (additionalInfo.coveragePlan.coverage || additionalInfo.coveragePlan.transitions || additionalInfo.coveragePlan.editorialIntent) && (
                <div className="nw-info-block">
                  <div className="nw-label">Coverage Plan</div>
                  <ul className="nw-list">
                    {additionalInfo.coveragePlan.coverage && <li><span className="nw-list-label">Coverage:</span> {additionalInfo.coveragePlan.coverage}</li>}
                    {additionalInfo.coveragePlan.transitions && <li><span className="nw-list-label">Transitions:</span> {additionalInfo.coveragePlan.transitions}</li>}
                    {additionalInfo.coveragePlan.editorialIntent && <li><span className="nw-list-label">Editorial Intent:</span> {additionalInfo.coveragePlan.editorialIntent}</li>}
                  </ul>
                </div>
              )}

              {additionalInfo.lightingPlan && (
                <div className="nw-info-block">
                  <div className="nw-label">Lighting Plan</div>
                  <div className="nw-value pre-wrap">{additionalInfo.lightingPlan}</div>
                </div>
              )}

              {additionalInfo.characterExperessions && additionalInfo.characterExperessions.length > 0 && (
                <div className="nw-info-block">
                  <div className="nw-label">Character Emotions</div>
                  <ul className="nw-list">
                    {additionalInfo.characterExperessions.map((ce, i) => (<li key={i}>{ce}</li>))}
                  </ul>
                </div>
              )}

              {additionalInfo.characterAttire && additionalInfo.characterAttire.length > 0 && (
                <div className="nw-info-block">
                  <div className="nw-label">Character Attire</div>
                  <ul className="nw-list">
                    {additionalInfo.characterAttire.map((ca, i) => (<li key={i}>{ca}</li>))}
                  </ul>
                </div>
              )}

              {Array.isArray(shotMatrix) && shotMatrix.length > 0 && (
                <>
                  <div className="nw-label" style={{ marginBottom: 8 }}>Shot Matrix</div>
                  {typeof shotMatrix[0] === 'string' ? (
                    (() => {
                      // Enhanced Parser for specific text format shown in screenshot
                      const parsedShots = [];
                      let currentShot = null;
                      let currentBeat = null;

                      shotMatrix.forEach(line => {
                        const trimmed = line.trim();
                        if (!trimmed) return;

                        // 1. Detect Beat
                        const beatMatch = trimmed.match(/^Beat\s+(\d+)[:]?/i);
                        if (beatMatch) {
                          currentBeat = beatMatch[1];
                          return;
                        }

                        // 2. Detect Shot Line: "Shot 1A: Low Angle... [6s]"
                        // Regex: Shot (ID): (Description) [(Duration)]
                        const shotMatch = trimmed.match(/^Shot\s+([0-9A-Z]+)[:\.]?\s*(.*?)(?:\[(.*?)\])?$/i);
                        if (shotMatch) {
                          if (currentShot) parsedShots.push(currentShot); // push previous
                          currentShot = {
                            beat: currentBeat,
                            id: shotMatch[1],
                            techSpec: shotMatch[2].trim(), // "Low Angle Tracking..."
                            duration: shotMatch[3] ? shotMatch[3].trim() : '',
                            prompt: '',
                            audio: '',
                            microMotion: '',
                            rawLines: [] // fallback
                          };
                          return;
                        }

                        // 3. Detect Bullets
                        if (currentShot) {
                          if (trimmed.startsWith('• Prompt:') || trimmed.startsWith('- Prompt:')) {
                            currentShot.prompt = trimmed.replace(/^[•-]\s*Prompt:\s*/i, '').trim();
                          } else if (trimmed.startsWith('• Audio:') || trimmed.startsWith('- Audio:')) {
                            currentShot.audio = trimmed.replace(/^[•-]\s*Audio:\s*/i, '').trim();
                          } else if (trimmed.startsWith('• Micro-motion:') || trimmed.startsWith('- Micro-motion:')) {
                            currentShot.microMotion = trimmed.replace(/^[•-]\s*Micro-motion:\s*/i, '').trim();
                          } else {
                            currentShot.rawLines.push(trimmed);
                          }
                        }
                      });
                      // push last
                      if (currentShot) parsedShots.push(currentShot);

                      return (
                        <div className="nw-shot-matrix-styled">
                          {parsedShots.map((shot, idx) => (
                            <div key={idx} className="nw-shot-card">
                              <div className="nw-shot-header">
                                <span className="nw-shot-id">Shot {shot.id}</span>
                                {shot.duration && <span className="nw-shot-duration">{shot.duration}</span>}
                              </div>
                              <div className="nw-shot-tech">{shot.techSpec}</div>

                              {shot.prompt && (
                                <div className="nw-shot-detail">
                                  <span className="label">Visual:</span> {shot.prompt}
                                </div>
                              )}
                              {shot.audio && (
                                <div className="nw-shot-detail">
                                  <span className="label">Audio:</span> {shot.audio}
                                </div>
                              )}
                              {shot.microMotion && (
                                <div className="nw-shot-detail">
                                  <span className="label">Motion:</span> {shot.microMotion}
                                </div>
                              )}

                              {/* fallback for unparsed lines */}
                              {shot.rawLines.length > 0 && (
                                <div className="nw-shot-raw">
                                  {shot.rawLines.map((l, i) => <div key={i}>{l}</div>)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()
                  ) : (
                    // Structured Object Rendering (Restored Table Logic)
                    <table className="shot-matrix-table">
                      <ShotMatrixHeader />
                      <tbody>
                        {shotMatrix.map((row, i) =>
                          (Array.isArray(row?.shots) ? row.shots : []).map((sh, j) => (
                            <tr key={`${i}-${j}`}>
                              <td className="sm-beat">{row?.beat}</td>
                              <td className="sm-id-cell">
                                <span className="sm-id-badge">{sh?.id}</span>
                              </td>
                              <td className="sm-beat">
                                {sh?.duration_sec ? `${sh.duration_sec}s` : '--'}
                              </td>
                              <td className="sm-specs">
                                {sh?.size || sh?.angle ? `${sh?.size || ''} ${sh?.angle || ''}` : '--'}
                              </td>
                              <td className="sm-dim" style={{ fontSize: '0.85em' }}>
                                {sh?.lens_characteristics && <div>{sh.lens_characteristics}</div>}
                                {sh?.depth_of_field && <div>{sh.depth_of_field}</div>}
                                {!sh?.lens_characteristics && !sh?.depth_of_field && '--'}
                              </td>
                              <td className="sm-dim" style={{ fontSize: '0.85em' }}>
                                {sh?.exposure_style && <div>{sh.exposure_style}</div>}
                                {sh?.post_grade_intent && <div>{sh.post_grade_intent}</div>}
                                {!sh?.exposure_style && !sh?.post_grade_intent && '--'}
                              </td>
                              <td className="sm-dim">
                                {sh?.camera_motion_vector || sh?.transition || '--'}
                              </td>
                              <td className="sm-details-full">
                                {sh?.prompt || '--'}
                              </td>
                              <td className="sm-details-full">
                                {sh?.dialogue ? `"${sh.dialogue}"` : '--'}
                              </td>
                              <td className="sm-dim">
                                {Array.isArray(sh?.audio) ? sh.audio.join(', ') : (sh?.audio || '--')}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </>
              )}

              {additionalInfo.dialogues && additionalInfo.dialogues.length > 0 && (
                <>
                  <div className="nw-label" style={{ marginBottom: 8 }}>Dialogues</div>
                  <ul className="scenes-audio-list">
                    {additionalInfo.dialogues.map((dialogue, i) => (
                      <li key={i} className="dialogue-container">
                        <div className="audio-container">
                          {dialogue.audio ? (
                            <audio controls>
                              <source src={dialogue.audio} type="audio/mpeg" />
                              Your browser does not support the audio element.
                            </audio>
                          ) : (
                            <audio controls disabled style={{ opacity: 0.5, filter: 'grayscale(1)', pointerEvents: 'none' }} />
                          )}
                          <div className="dialogue-text">{dialogue.text || dialogue.line || ''}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {additionalInfo.music && (additionalInfo.music.audio || additionalInfo.music.text) && (
                <>
                  <div className="nw-label" style={{ marginBottom: 8 }}>Background Music</div>
                  <div className="audio-container">
                    <MiniPlayer src={additionalInfo.music.audio} disabled={!additionalInfo.music.audio} />
                    <div className="nw-value">{additionalInfo.music.text}</div>
                  </div>
                </>
              )}

            </>
          )}

        </div>

        <div className="nw-scene-details-right">
          {!sceneData.video && (
            <button
              disabled={!!videoIsLoading[index]}
              className={videoIsLoading[index] ? 'video-generating nw-gen-btn' : 'nw-gen-btn'}
              onClick={(e) => {
                e.stopPropagation(); // Prevent toggling the details
                generateVideo?.(index);
              }}
            >
              {videoIsLoading[index]
                ? `Generating${(videoDots[index] || '').padEnd(3, ' ')}`
                : 'Generate Video ▶'}
            </button>
          )}
        </div>

      </div >
    );
  };

  return (
    <div className="narrative-workbench" role="region" aria-label="Narrative Workbench">
      <div className="nw-body">
        {/* ───────────────── Screenplay Panel ───────────────── */}
        <section className="nw-panel" aria-label="Screenplay Panel">
          <header className="nw-panel-header">
            <div className="nw-title-wrap">
              <button
                type="button"
                className="nw-collapse-btn"
                onClick={() => setScreenplayCollapsed(v => !v)}
                aria-expanded={!isScreenplayCollapsed}
                aria-controls="nw-screenplay-body"
                title={isScreenplayCollapsed ? 'Expand' : 'Collapse'}
              >
                {isScreenplayCollapsed ? '▶' : '▼'}
              </button>
              <h3 className="nw-panel-title">Screenplay</h3>
              {isDetailsGenerating && (
                <span className="nw-badge" style={{ marginLeft: 8 }}>
                  Generating details…
                  {loadingGifUrl && <img src={loadingGifUrl} alt="" className="nw-loading" />}
                </span>
              )}
            </div>

            <div className="nw-panel-actions">
              <button title="Regenerate" className="nw-tool-btn" onClick={onRegenerateScreenplay} disabled={isScreenplayLoading} aria-label="Regenerate Screenplay">⟳</button>
              <button title="Edit" className="nw-tool-btn" onClick={onEditScreenplay} aria-pressed={!!isEditing} aria-label="Edit Screenplay">✎</button>
              <button title="Download" className="nw-tool-btn" onClick={onDownloadScreenplay} aria-label="Download Screenplay">⤓</button>
              <button title="Enhance" className="nw-tool-btn" onClick={onEnhanceScreenplay} aria-label="Enhance Screenplay">🪄</button>
            </div>
          </header>

          {!isScreenplayCollapsed && (
            <>
              <div ref={screenplayTileHostRef} id="nw-screenplay-body" className="nw-panel-content">
                <ScreenplayTile
                  detailedScreenplay={detailedScreenplay}
                  screenplayOutline={screenplayOutline}
                  isDetailsGenerating={isDetailsGenerating}
                  isLoading={isScreenplayLoading}
                  onGenerateScenes={onGenerateScenes}
                  isGeneratingScenes={isScenesGenerating}
                  regenerateVideo={regenerateVideo}
                  regenerateVideoPostEdit={regenerateVideoPostEdit}
                  imageModels={modelOptions?.image || []} // Pass available image models
                  isEditing={isEditing}
                  onSaveEditedScreenplay={onSaveEditedScreenplay}
                  maximizeScreenPlay={maximizeScreenPlay}
                  toggleMaximizeScreenPlay={toggleMaximizeScreenPlay}
                  characterBible={characterBible}
                  onCancelEdit={onCancelEdit}
                  onValidateContent={onValidateContent}
                  hydratedScenesByNumber={hydratedScenesByNumber}
                  isExpanded={screenplayExpanded}
                />
              </div>

              {maximizeScreenPlay && screenplayHasMore && (
                <footer className="nw-panel-footer">
                  <button className="nw-gen-btn" onClick={toggleScreenplayExpand} aria-expanded={screenplayExpanded}>
                    {screenplayExpanded ? 'Show Less' : 'Show More'}
                  </button>
                </footer>
              )}
            </>
          )}
        </section>

        {/* ───────────────── Characters Panel (gated) ───────────────── */}
        {showCharactersPanel && (
          <section className="nw-panel" aria-label="Characters Panel">
            <header className="nw-panel-header">
              <div className="nw-title-wrap">
                <button
                  type="button"
                  className="nw-collapse-btn"
                  onClick={() => setCharactersCollapsed(v => !v)}
                  aria-expanded={!isCharactersCollapsed}
                  aria-controls="nw-characters-body"
                  title={isCharactersCollapsed ? 'Expand' : 'Collapse'}
                >
                  {isCharactersCollapsed ? '▶' : '▼'}
                </button>
                <h3 className="nw-panel-title">Characters</h3>
              </div>
              <div className="nw-panel-actions" />
            </header>

            {!isCharactersCollapsed && (
              <div id="nw-characters-body" className="nw-panel-content">
                {isCharacterBibleLoading ? (
                  <div className="nw-empty">Generating Character Bible…
                    <img src={loadingGifUrl} alt="Loading…" className="nw-loading" />
                  </div>
                ) : charactersForView.length > 0 ? (
                  <div className="character-bible-container">
                    <div className="horizontal-character-bible-container">
                      <CharacterSlideshow
                        characters={charactersForView}
                        setIsOverlayVisible={setIsOverlayVisible}
                        onSaveCharacter={onSaveCharacter}
                        onRegenerateCharacterFull={onRegenerateCharacterFull} // NEW
                        characterIsRegenerating={characterIsRegenerating}     // NEW
                        imageModels={modelOptions?.image || []}               // NEW
                        projectId={projectId}
                        onValidateContent={onValidateContent}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="nw-empty">No characters yet.</div>
                )}
              </div>
            )}
          </section>
        )}

        {/* ───────────────── Scenes Panel (gated) ───────────────── */}
        {shouldRenderScenesPanel && (
          <section className="nw-panel" aria-label="Scenes Panel">
            <header className="nw-panel-header">
              <div className="nw-title-wrap">
                <button
                  type="button"
                  className="nw-collapse-btn"
                  onClick={() => setScenesCollapsed(v => !v)}
                  aria-expanded={!isScenesCollapsed}
                  aria-controls="nw-scenes-body"
                  title={isScenesCollapsed ? 'Expand' : 'Collapse'}
                >
                  {isScenesCollapsed ? '▶' : '▼'}
                </button>
                <h3 className="nw-panel-title">Scenes</h3>
              </div>

              <div className="nw-panel-actions">
                {_isScenesGenerating ? (
                  loadingGifUrl ? (
                    <img src={loadingGifUrl} alt="Loading…" className="nw-loading" />
                  ) : (
                    <span className="nw-badge">Generating…</span>
                  )
                ) : null}
              </div>
            </header>

            {!isScenesCollapsed && (
              <div id="nw-scenes-body" className="nw-panel-content">

                {_isScenesGenerating ? (
                  loadingGifUrl ? (
                    <div className="nw-empty">Generating Scenes…
                      <img src={loadingGifUrl} alt="Loading…" className="nw-loading" />
                    </div>
                  ) : (
                    <span className="nw-badge">Generating…</span>
                  )
                ) : null}

                {showScenes && !_isScenesGenerating ? (
                  <div className="nw-scenes-container">
                    {scenes.map((sceneData, index) => {
                      const hasVideo = !!sceneData.video || !!videoIsLoading[index];
                      // Lifted State: Default to 'video' mode if video exists, else 'image'
                      const uiState = sceneUIStateRef.current[index] || {
                        mode: hasVideo ? 'video' : 'image', // intelligent default
                        showHistory: false,
                        showSlideshow: false
                      };

                      // Helper to update specific field for this scene
                      const setUI = (key, val) => {
                        // Ensure we start with the current ephemeral state as base if ref is empty
                        const base = sceneUIStateRef.current[index] || {
                          mode: hasVideo ? 'video' : 'image',
                          showHistory: false,
                          showSlideshow: false
                        };
                        const next = { ...base, [key]: val };
                        // If switching mode, ensure we default to the other if needed, but basic toggle is enough
                        sceneUIStateRef.current = { ...sceneUIStateRef.current, [index]: next };
                        forceUpdate(); // simpler than deep re-render management
                      };

                      const isVideoMode = uiState.mode === 'video';
                      if (!hasVideo && isVideoMode) {
                        // fallback if mode says video but video removed?
                        // actually just let it stay, buttons will just be disabled or empty
                      }

                      return (
                        <div className={`nw-horizontal-scenes-row${hasVideo ? ' has-video' : ''}`} key={index}>
                          {/* LEFT: column that holds the pair (image+video) and the details under it */}
                          <div className="nw-scene-column">
                            {hasVideo ? (
                              <>
                                {/* PAIR ONLY WHEN VIDEO EXISTS */}
                                <div className="nw-scene-pair">
                                  <SceneTile
                                    image={sceneData.image}
                                    title={sceneData.title}
                                    duration={sceneData.duration}
                                    summary={sceneData.summary}
                                    additionalInfo={sceneData.additionalInfo}
                                    onRegenerateImage={(modelOverride) => regenerateImage?.(index, modelOverride)}
                                    onRegenerateImagePostEdit={(p) => regenerateImagePostEdit?.(index, p)}
                                    onGenerateVideo={() => generateVideo?.(index)}
                                    isVideoGenerating={!!videoIsLoading[index]}
                                    videoGeneratingDots={videoDots[index] || ''}
                                    isImageRegenerating={!!imageIsRegenerating[index]}
                                    imageHistory={sceneData.imageHistory}
                                    onSelectImage={(url) => onSelectSceneImage(index, url)}
                                    imageModels={modelOptions?.image || []}
                                    compact
                                    // Controlled Props
                                    showHistory={!isVideoMode && uiState.showHistory}
                                    onToggleHistory={() => setUI('showHistory', !uiState.showHistory)}
                                    activeMode={uiState.mode}
                                  />
                                  <VideoTile
                                    video={sceneData.video}
                                    title={sceneData.title}
                                    duration={sceneData.duration}
                                    summary={sceneData.summary}
                                    additionalInfo={sceneData.additionalInfo}
                                    onRegenerateVideo={() => regenerateVideo?.(index)}
                                    onRegenerateVideoPostEdit={(p) => regenerateVideoPostEdit?.(index, p)}
                                    onRegenerateShot={(shotId, p) => onRegenerateShot?.(index, shotId, p)}
                                    onSelectShotVideo={(shotId, url) => onSelectShotVideo?.(index, shotId, url)}
                                    loading={!!videoIsLoading[index]}
                                    isMerging={!!isMerging?.[index]} // NEW: Pass specific scene merge state
                                    // Controlled Props
                                    showHistory={isVideoMode && uiState.showHistory}
                                    onToggleHistory={() => setUI('showHistory', !uiState.showHistory)}
                                    showSlideshow={uiState.showSlideshow}
                                    onToggleSlideshow={() => setUI('showSlideshow', !uiState.showSlideshow)}
                                    activeMode={uiState.mode}
                                    onSlideChange={(shotId) => {
                                      const prev = sceneUIStateRef.current[index] || {};
                                      if (prev.activeShotId !== shotId) {
                                        sceneUIStateRef.current[index] = { ...prev, activeShotId: shotId };
                                        forceUpdate(); // Re-render to update toolbar state (History btn)
                                      }
                                    }}
                                  />
                                </div>
                                <SceneDetailsStrip sceneData={sceneData} index={index} />
                              </>
                            ) : (
                              <>
                                {/* SOLO SCENE */}
                                <SceneTile
                                  image={sceneData.image}
                                  title={sceneData.title}
                                  duration={sceneData.duration}
                                  summary={sceneData.summary}
                                  additionalInfo={sceneData.additionalInfo}
                                  onRegenerateImage={(modelOverride) => regenerateImage?.(index, modelOverride)}
                                  onRegenerateImagePostEdit={(p) => regenerateImagePostEdit?.(index, p)}
                                  onGenerateVideo={() => generateVideo?.(index)}
                                  isVideoGenerating={!!videoIsLoading[index]}
                                  videoGeneratingDots={videoDots[index] || ''}
                                  isImageRegenerating={!!imageIsRegenerating[index]}
                                  imageHistory={sceneData.imageHistory}
                                  onSelectImage={(url) => onSelectSceneImage(index, url)}
                                  imageModels={modelOptions?.image || []}
                                  // Controlled Props
                                  showHistory={uiState.showHistory}
                                  onToggleHistory={() => setUI('showHistory', !uiState.showHistory)}
                                  activeMode="image"
                                />
                                <SceneDetailsStrip sceneData={sceneData} index={index} />
                              </>
                            )}
                          </div>

                          {/* RIGHT: Context-Aware Vertical Toolbar */}
                          <div className="nw-vertical-buttons">

                            {hasVideo && (
                              <div className="mode-toggle-pill">
                                <button
                                  className={`mode-btn ${!isVideoMode ? 'active' : ''}`}
                                  onClick={() => setUI('mode', 'image')}
                                  title="Image Mode"
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                    <polyline points="21 15 16 10 5 21"></polyline>
                                  </svg>
                                </button>
                                <button
                                  className={`mode-btn ${isVideoMode ? 'active' : ''}`}
                                  onClick={() => setUI('mode', 'video')}
                                  title="Video Mode"
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="23 7 16 12 23 17 23 7"></polygon>
                                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                                  </svg>
                                </button>
                              </div>
                            )}

                            {/* 2. REGENERATE (Primary Action) */}
                            {(() => {
                              const isShotMode = isVideoMode && uiState.showSlideshow && uiState.activeShotId;
                              const isSceneRegenDisabled = isVideoMode && !isShotMode;

                              let title = "Regenerate Image";
                              if (isVideoMode) {
                                title = isShotMode ? "Regenerate Shot" : "Regeneration available for individual shots";
                              }

                              return (
                                <button
                                  title={title}
                                  className={`toolbar-button emphasize ${imageIsRegenerating[index] || videoIsLoading[index] ? 'spin-animation' : ''}`}
                                  style={{
                                    fontSize: '1.8em',
                                    opacity: isSceneRegenDisabled ? 0.3 : 1,
                                    cursor: isSceneRegenDisabled ? 'not-allowed' : 'pointer'
                                  }}
                                  disabled={isSceneRegenDisabled}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isVideoMode) {
                                      if (isShotMode) {
                                        onRegenerateShot?.(index, uiState.activeShotId);
                                      }
                                    } else {
                                      regenerateImage?.(index);
                                    }
                                  }}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    // Only show context menu for Image mode currently, as per regression report
                                    if (!isVideoMode && modelOptions?.image?.length > 0) {
                                      const menuWidth = 160;
                                      const overflow = e.clientX + menuWidth > window.innerWidth;
                                      const x = overflow ? e.clientX - menuWidth : e.clientX;
                                      setContextMenu({
                                        x,
                                        y: e.clientY,
                                        options: modelOptions.image,
                                        onSelect: (alias) => regenerateImage?.(index, alias)
                                      });
                                    }
                                  }}
                                >
                                  ⟳
                                </button>
                              );
                            })()}

                            {/* 3. HISTORY (Context Aware & Disabled if Empty) */}
                            {(() => {
                              // Calculate availability
                              const hasImgHist = sceneData.imageHistory?.length > 0;
                              let hasVidHist = false;
                              // User Requirement: History only active in Shots Mode (Slideshow)
                              if (isVideoMode) {
                                if (uiState.showSlideshow && uiState.activeShotId && sceneData.additionalInfo?.shotMatrix) {
                                  // Specific shot history
                                  const flatShots = sceneData.additionalInfo.shotMatrix.flatMap(r => r.shots || []);
                                  const shot = flatShots.find(s => String(s.id) === String(uiState.activeShotId));
                                  hasVidHist = shot?.videoHistory?.length > 0;
                                } else {
                                  // In Scene mode, history is disabled as per user request
                                  hasVidHist = false;
                                }
                              }

                              const hasHistory = isVideoMode ? hasVidHist : hasImgHist;

                              return (
                                <button
                                  title={hasHistory ? (isVideoMode ? "Video History" : "Image History") : "No History Available"}
                                  disabled={!hasHistory}
                                  className={`toolbar-button ${uiState.showHistory ? 'active' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); setUI('showHistory', !uiState.showHistory); }}
                                  style={{ opacity: hasHistory ? 1 : 0.3, cursor: hasHistory ? 'pointer' : 'default' }}
                                >
                                  🕐
                                </button>
                              );
                            })()
                            }

                            {/* 4. Slideshow / View Mode (VIDEO ONLY) - "Stacked Cards" Icon */}
                            {
                              isVideoMode && (
                                <button
                                  title={uiState.showSlideshow ? "Show Merged Video" : "Show Individual Shots"}
                                  className={`toolbar-button ${uiState.showSlideshow ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation(); // Stop bubbling to prevent mode switch
                                    setUI('showSlideshow', !uiState.showSlideshow);
                                  }}
                                >
                                  {/* Stacked shots icon */}
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 6h12v10H4z" /> {/* Front card */}
                                    <path d="M8 4h12v10" />     {/* Middle card (offset) */}
                                    <path d="M12 2h10v10" />    {/* Back card (offset) */}
                                    <polygon points="9 9 13 11 9 13 9 9" fill="currentColor" stroke="none" /> {/* Play Triangle */}
                                  </svg>
                                </button>
                              )
                            }

                            {/* 5. RESTORED BUTTONS (Edit, Lock, Download, Save, Delete, Upscale, Enhance) */}
                            <button title="Edit" className="toolbar-button" onClick={(e) => { e.stopPropagation(); }}>✎</button>
                            <button title="Lock" className="toolbar-button" onClick={(e) => { e.stopPropagation(); }}>🔒</button>
                            <button title="Download" className="toolbar-button" onClick={(e) => { e.stopPropagation(); }}>⤓</button>
                            <button title="Save" className="toolbar-button" onClick={(e) => { e.stopPropagation(); }}>💾</button>
                            <button title="Delete" className="toolbar-button delete" onClick={(e) => { e.stopPropagation(); }}>🗑</button>
                            <button title="Upscale" className="toolbar-button" onClick={(e) => { e.stopPropagation(); }}>👁</button>
                            <button title="Enhance" className="toolbar-button" onClick={(e) => { e.stopPropagation(); }}>🪄</button>

                          </div>

                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            )}
          </section>
        )}

        {/* ───────────────── Final Movie Panel (gated) ───────────────── */}
        {showFinalMoviePanel && mergedVideoUrl && (
          <section className="nw-panel" aria-label="Final Movie Panel">
            <header className="nw-panel-header">
              <div className="nw-title-wrap">
                <button
                  type="button"
                  className="nw-collapse-btn"
                  onClick={() => setFinalCollapsed(v => !v)}
                  aria-expanded={!isFinalCollapsed}
                  aria-controls="nw-final-body"
                  title={isFinalCollapsed ? 'Expand' : 'Collapse'}
                >
                  {isFinalCollapsed ? '▶' : '▼'}
                </button>
                <h3 className="nw-panel-title">Final Movie </h3>
              </div>
              <div className="nw-panel-actions">
                <button
                  title="Remerge All Scenes"
                  className={`nw-tool-btn ${isGlobalMerging ? 'spin-animation' : ''}`}
                  onClick={onMergeScenes}
                  disabled={isGlobalMerging}
                >
                  {/* Merge Icon: Two arrows merging into one up arrow */}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {/* Horizontal Merge Icon */}
                    <path d="M4 18h4c2 0 4-6 7-6h5" />
                    <path d="M4 6h4c2 0 4 6 7 6h5" />
                    <polyline points="18 9 21 12 18 15" />
                  </svg>
                </button>
                <button title="Download" className="nw-tool-btn" onClick={() => window.open(mergedVideoUrl, '_blank')}>⤓</button>
              </div>
            </header>

            {!isFinalCollapsed && (
              <div id="nw-final-body" className="nw-panel-content">
                <div className="nw-final-movie">
                  <MergedVideoTile videoUrl={mergedVideoUrl} title={finalMovieTitle || ''} />
                </div>
              </div>
            )}
          </section>
        )}
      </div>

      {/* Context Menu Portal */}
      {contextMenu && (
        <div
          className="scene-tile-context-backdrop"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998 }}
          onClick={() => setContextMenu(null)}
        />
      )}
      {contextMenu && (
        <div
          className="scene-tile-context-menu"
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 9999,
            background: '#222',
            border: '1px solid #444',
            borderRadius: '8px',
            padding: '8px',
            minWidth: '150px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
          }}
        >
          <div className="context-menu-header" style={{ marginBottom: '8px', fontWeight: 'bold', color: '#888', fontSize: '0.85em', padding: '0 8px' }}>Select Model</div>
          {contextMenu.options.map(m => (
            <div
              key={m.alias}
              className="context-menu-item"
              style={{ padding: '6px 8px', cursor: 'pointer', borderRadius: '4px', fontSize: '0.9em' }}
              onClick={() => {
                contextMenu.onSelect(m.alias);
                setContextMenu(null);
              }}
              onMouseEnter={(e) => e.target.style.background = '#333'}
              onMouseLeave={(e) => e.target.style.background = 'transparent'}
            >
              {m.label || m.alias}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
export default React.memo(NarrativeWorkbench);
